import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronDown, BookOpen, FileText, Loader2 } from 'lucide-react';
import { getCourses, getCourseUnits, getUnitSentences } from '../services/courses';
import { CourseInfo, UnitInfo, CourseSentence } from '../types';
import { useSoundHandlers } from '../hooks/useSoundEffects';
import { useTheme } from '../contexts/ThemeContext';

// 选中的句子完整信息
export interface SelectedSentenceInfo {
  sentenceId: string;
  unitId: string;
  unitName: string;
  courseId: string;
  courseName: string;
  orderNum: number;
}

interface CourseSelectorProps {
  onSentenceSelect: (sentence: string, info?: SelectedSentenceInfo) => void;
  disabled?: boolean;
}

export default function CourseSelector({ onSentenceSelect, disabled = false }: CourseSelectorProps) {
  const { playHover, playClick } = useSoundHandlers();
  const { theme } = useTheme();
  const isQinghua = theme === 'qinghua';
  
  // 数据状态
  const [courses, setCourses] = useState<CourseInfo[]>([]);
  const [units, setUnits] = useState<UnitInfo[]>([]);
  const [sentences, setSentences] = useState<CourseSentence[]>([]);
  
  // 选中状态
  const [selectedCourse, setSelectedCourse] = useState<CourseInfo | null>(null);
  const [selectedUnit, setSelectedUnit] = useState<UnitInfo | null>(null);
  const [selectedSentence, setSelectedSentence] = useState<CourseSentence | null>(null);
  
  // 下拉展开状态
  const [courseDropdownOpen, setCourseDropdownOpen] = useState(false);
  const [unitDropdownOpen, setUnitDropdownOpen] = useState(false);
  
  // 加载状态
  const [loadingCourses, setLoadingCourses] = useState(true);
  const [loadingUnits, setLoadingUnits] = useState(false);
  const [loadingSentences, setLoadingSentences] = useState(false);
  const [error, setError] = useState('');

  // 加载课程列表
  useEffect(() => {
    async function loadCourses() {
      try {
        setLoadingCourses(true);
        const data = await getCourses();
        setCourses(data);
      } catch (err) {
        setError('加载课程失败');
        console.error(err);
      } finally {
        setLoadingCourses(false);
      }
    }
    loadCourses();
  }, []);

  // 选择课程后加载单元
  const handleCourseSelect = async (course: CourseInfo) => {
    playClick();
    setSelectedCourse(course);
    setSelectedUnit(null);
    setSelectedSentence(null);
    setSentences([]);
    setCourseDropdownOpen(false);
    
    try {
      setLoadingUnits(true);
      const data = await getCourseUnits(course.id);
      setUnits(data);
    } catch (err) {
      setError('加载单元失败');
      console.error(err);
    } finally {
      setLoadingUnits(false);
    }
  };

  // 选择单元后加载句子
  const handleUnitSelect = async (unit: UnitInfo) => {
    playClick();
    setSelectedUnit(unit);
    setSelectedSentence(null);
    setUnitDropdownOpen(false);
    
    try {
      setLoadingSentences(true);
      const data = await getUnitSentences(unit.id);
      setSentences(data);
    } catch (err) {
      setError('加载句子失败');
      console.error(err);
    } finally {
      setLoadingSentences(false);
    }
  };

  // 选择句子
  const handleSentenceClick = (sentence: CourseSentence) => {
    playClick();
    setSelectedSentence(sentence);
    
    // 传递完整的句子信息
    const info: SelectedSentenceInfo | undefined = selectedCourse && selectedUnit ? {
      sentenceId: sentence.id,
      unitId: selectedUnit.id,
      unitName: selectedUnit.name,
      courseId: selectedCourse.id,
      courseName: selectedCourse.name,
      orderNum: sentence.order_num,
    } : undefined;
    
    onSentenceSelect(sentence.text, info);
  };

  // 下拉选择器组件
  const Dropdown = ({ 
    label, 
    value, 
    placeholder,
    options, 
    isOpen, 
    setIsOpen, 
    onSelect,
    loading,
    disabled: dropdownDisabled,
    renderOption
  }: {
    label: string;
    value: string | null;
    placeholder: string;
    options: { id: string; display: string }[];
    isOpen: boolean;
    setIsOpen: (open: boolean) => void;
    onSelect: (option: { id: string; display: string }) => void;
    loading?: boolean;
    disabled?: boolean;
    renderOption?: (option: { id: string; display: string }) => React.ReactNode;
  }) => (
    <div className="relative">
      <label 
        className="block text-sm mb-2"
        style={{ 
          color: isQinghua ? '#4A5568' : 'var(--theme-text-secondary)',
          fontFamily: isQinghua ? "'Noto Serif SC', serif" : undefined
        }}
      >
        {label}
      </label>
      <button
        onClick={() => { if (!dropdownDisabled && !disabled) { playClick(); setIsOpen(!isOpen); } }}
        onMouseEnter={playHover}
        disabled={dropdownDisabled || disabled}
        className="w-full flex items-center justify-between px-4 py-3 rounded-lg transition-all duration-300"
        style={{
          background: isQinghua ? 'rgba(248, 244, 232, 0.8)' : 'var(--theme-glow-color)',
          border: `1px solid ${isQinghua ? 'rgba(46, 90, 168, 0.2)' : 'var(--theme-border)'}`,
          color: value 
            ? (isQinghua ? '#2C3E50' : 'var(--theme-text-primary)')
            : (isQinghua ? '#718096' : 'var(--theme-text-muted)'),
          fontFamily: isQinghua ? "'Noto Serif SC', serif" : undefined,
          opacity: dropdownDisabled || disabled ? 0.5 : 1,
          cursor: dropdownDisabled || disabled ? 'not-allowed' : 'pointer'
        }}
      >
        <span>{loading ? '加载中...' : (value || placeholder)}</span>
        {loading ? (
          <Loader2 className="w-4 h-4 animate-spin" style={{ color: 'var(--theme-accent)' }} />
        ) : (
          <ChevronDown 
            className={`w-4 h-4 transition-transform ${isOpen ? 'rotate-180' : ''}`}
            style={{ color: isQinghua ? '#2E5AA8' : 'var(--theme-accent)' }}
          />
        )}
      </button>
      
      <AnimatePresence>
        {isOpen && options.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="absolute z-50 w-full mt-2 rounded-lg overflow-hidden"
            style={{
              background: isQinghua ? '#FFFDF8' : 'var(--theme-bg-card)',
              border: `1px solid ${isQinghua ? 'rgba(46, 90, 168, 0.2)' : 'var(--theme-border)'}`,
              boxShadow: isQinghua 
                ? '0 10px 30px rgba(46, 90, 168, 0.15)' 
                : '0 10px 30px rgba(0, 0, 0, 0.3)'
            }}
          >
            {options.map((option) => (
              <button
                key={option.id}
                onClick={() => onSelect(option)}
                onMouseEnter={playHover}
                className="w-full px-4 py-3 text-left transition-all duration-200"
                style={{
                  color: isQinghua ? '#2C3E50' : 'var(--theme-text-primary)',
                  fontFamily: isQinghua ? "'Noto Serif SC', serif" : undefined,
                  background: 'transparent'
                }}
                onMouseOver={(e) => {
                  e.currentTarget.style.background = isQinghua 
                    ? 'rgba(46, 90, 168, 0.08)' 
                    : 'var(--theme-glow-color)';
                }}
                onMouseOut={(e) => {
                  e.currentTarget.style.background = 'transparent';
                }}
              >
                {renderOption ? renderOption(option) : option.display}
              </button>
            ))}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );

  return (
    <div className="space-y-6">
      {/* 错误提示 */}
      {error && (
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="text-sm"
          style={{ color: isQinghua ? '#c24b40' : '#ef4444' }}
        >
          {error}
        </motion.p>
      )}

      {/* 课程选择 */}
      <Dropdown
        label="选择课程"
        value={selectedCourse?.name || null}
        placeholder="请选择课程..."
        options={courses.map(c => ({ id: c.id, display: c.name }))}
        isOpen={courseDropdownOpen}
        setIsOpen={setCourseDropdownOpen}
        onSelect={(option) => {
          const course = courses.find(c => c.id === option.id);
          if (course) handleCourseSelect(course);
        }}
        loading={loadingCourses}
        renderOption={(option) => {
          const course = courses.find(c => c.id === option.id);
          return (
            <div className="flex items-center gap-2">
              <BookOpen className="w-4 h-4" style={{ color: isQinghua ? '#2E5AA8' : 'var(--theme-accent)' }} />
              <span>{option.display}</span>
              {course?.type === 'selected' && (
                <span 
                  className="text-xs px-2 py-0.5 rounded"
                  style={{ 
                    background: isQinghua ? 'rgba(201, 169, 97, 0.2)' : 'var(--theme-glow-color)',
                    color: isQinghua ? '#C9A961' : 'var(--theme-accent)'
                  }}
                >
                  精选
                </span>
              )}
            </div>
          );
        }}
      />

      {/* 单元选择 */}
      <Dropdown
        label="选择单元"
        value={selectedUnit?.name || null}
        placeholder="请先选择课程..."
        options={units.map(u => ({ id: u.id, display: u.name }))}
        isOpen={unitDropdownOpen}
        setIsOpen={setUnitDropdownOpen}
        onSelect={(option) => {
          const unit = units.find(u => u.id === option.id);
          if (unit) handleUnitSelect(unit);
        }}
        loading={loadingUnits}
        disabled={!selectedCourse}
      />

      {/* 句子列表 */}
      {selectedUnit && (
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <label 
            className="block text-sm mb-3"
            style={{ 
              color: isQinghua ? '#4A5568' : 'var(--theme-text-secondary)',
              fontFamily: isQinghua ? "'Noto Serif SC', serif" : undefined
            }}
          >
            选择句子
          </label>
          
          {loadingSentences ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-6 h-6 animate-spin" style={{ color: 'var(--theme-accent)' }} />
            </div>
          ) : sentences.length === 0 ? (
            <p 
              className="text-center py-8"
              style={{ color: isQinghua ? '#718096' : 'var(--theme-text-muted)' }}
            >
              暂无句子
            </p>
          ) : (
            <div 
              className="space-y-2 max-h-[300px] overflow-y-auto pr-2"
              style={{ scrollbarWidth: 'thin' }}
            >
              {sentences.map((sentence, index) => (
                <motion.button
                  key={sentence.id}
                  initial={{ opacity: 0, x: -10 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.05 }}
                  whileHover={{ x: 4 }}
                  onClick={() => handleSentenceClick(sentence)}
                  onMouseEnter={playHover}
                  disabled={disabled}
                  className="w-full text-left p-4 rounded-lg transition-all duration-200"
                  style={{
                    background: selectedSentence?.id === sentence.id
                      ? (isQinghua ? 'rgba(46, 90, 168, 0.1)' : 'var(--theme-glow-strong)')
                      : (isQinghua ? 'rgba(248, 244, 232, 0.5)' : 'var(--theme-glow-color)'),
                    border: `1px solid ${
                      selectedSentence?.id === sentence.id
                        ? (isQinghua ? '#2E5AA8' : 'var(--theme-accent)')
                        : (isQinghua ? 'rgba(46, 90, 168, 0.15)' : 'var(--theme-border)')
                    }`,
                    opacity: disabled ? 0.5 : 1,
                    cursor: disabled ? 'not-allowed' : 'pointer'
                  }}
                >
                  <div className="flex items-start gap-3">
                    <div 
                      className="flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center text-xs"
                      style={{
                        background: isQinghua ? 'rgba(46, 90, 168, 0.1)' : 'var(--theme-glow-color)',
                        color: isQinghua ? '#2E5AA8' : 'var(--theme-accent)',
                        border: `1px solid ${isQinghua ? 'rgba(46, 90, 168, 0.3)' : 'var(--theme-border)'}`
                      }}
                    >
                      {sentence.order_num}
                    </div>
                    <p 
                      className="text-sm leading-relaxed"
                      style={{ 
                        color: isQinghua ? '#2C3E50' : 'var(--theme-text-primary)',
                        fontFamily: "'JetBrains Mono', monospace"
                      }}
                    >
                      {sentence.text}
                    </p>
                  </div>
                </motion.button>
              ))}
            </div>
          )}
        </motion.div>
      )}

      {/* 选中提示 */}
      {selectedSentence && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="flex items-center gap-2 text-sm"
          style={{ color: isQinghua ? '#2E5AA8' : 'var(--theme-accent)' }}
        >
          <FileText className="w-4 h-4" />
          <span style={{ fontFamily: isQinghua ? "'Noto Serif SC', serif" : undefined }}>
            已选择句子 {selectedSentence.order_num}
          </span>
        </motion.div>
      )}
    </div>
  );
}

